import { CURRICULUM_KEYS } from '@mathflat/domain/Curriculum'
import type { Revision, SchoolType } from '@mathflat/domain/SchoolSystem'
import type { SchoolTestOrigin, SchoolTestType } from '@mathflat/domain/SchoolTest'
import type { RecentlyDownloadedSchool } from '@mathflat/domain/SchoolTest'
import type { SchoolTestSearchType } from '@mathflat/domain/Worksheet'
import type { WorksheetFileParams } from '@mathflat/domain/Worksheet'
import { makeAutoObservable, runInAction } from 'mobx'

import { schoolTestController } from '@/controller/schoolTest.controller'
import { worksheetController } from '@/controller/worksheet.controller'
import { Worksheet } from '@/dto'
import { ReqParams } from '@/dto/common/ReqParams'
import type { SchoolTest } from '@/dto/SchoolTest/SchoolTest'
import { DropdownNode } from '@/modules/schoolTest/models/DropdownNode.model'
import { SchoolTestDropdownManager } from '@/modules/schoolTest/services/SchoolTestDropdownManager.service'
import { DropdownStore } from '@/modules/schoolTest/stores/Dropdown.store'
import { SchoolsUnderRegionStore } from '@/modules/schoolTest/stores/SchoolsUnderRegion.store'
import type { SchoolTestDropdownType } from '@/modules/schoolTest/types/dropdown.type'
import { getVisibleSchoolTestYears } from '@/modules/schoolTest/utils/visibleSchoolTestYears'
import { commonWorksheetService } from '@/service/commonWorksheet.service'
import { curriculumCoreService, type ExpendCoreItem } from '@/service/curriculumCore.service'
import { worksheetPrintService } from '@/service/print'
import { localStorageService } from '@/service/storage.service'
import WebviewService from '@/service/webview.service'
import appPostMsg from '@/utils/appPostMsg'
import { fileDownload, urlPreview } from '@/utils/download'
import { commonErrorMessage, toastSuccess } from '@/utils/toast'

import { WorksheetBySchoolTest } from '../models/SimilarWorksheetBySchoolTest.model'

/**
 * 내보내기, 미리보기 시 앱 API 호출 완료를 위해 일정 시간 지연
 * - 네이티브 앱에서 미리보기, 다운로드 동작시 현재 성공에 대한 콜백이 없어 웹에서 임의로 지연을 추가했습니다
 * - 지연 시간은 네이티브 앱이 이벤트를 받고 API를 콜하는 시간을 가정하여 추가했습니다
 */
const delayNativeAppApiCall = async () => {
  const NATIVE_APP_API_CALL_DELAY_TIME = 250
  await new Promise((resolve) => setTimeout(resolve, NATIVE_APP_API_CALL_DELAY_TIME))
}

export class WorksheetBySchoolTestService {
  static COLUMNS = [
    ['선택', '지역', '연도', '학년', '시험명'],
    ['기출업로드', '미리보기', '추천 학습지'],
  ]
  recentSchools: RecentlyDownloadedSchool[] = []

  constructor() {
    makeAutoObservable(this)
    this.loadSchools()
    this.loadRecentSchools()
  }

  async loadSchools() {
    const currentSchoolType =
      (this.dropdownManager.currentChecked.schoolType?.id as SchoolType) ?? 'MIDDLE'
    const trieKey =
      currentSchoolType === 'MIDDLE'
        ? this.dropdownManager.currentChecked.gradeSemester?.id
        : this.dropdownManager.currentChecked.highSchoolSubject?.id

    const currentGradeSemester =
      trieKey && trieKey !== 'ALL' ? curriculumCoreService.getItemByTrieKey(trieKey) : undefined

    this._schoolSearchManager.loadSchools({
      schoolType: currentSchoolType,
      grade: currentGradeSemester?.gradeSubject,
      semester: currentGradeSemester?.semester,
    })
  }

  // school-test pagination params
  private _pagination = new ReqParams({ params: { page: 0, size: 40, isLast: false } })
  // 필터 변경 시 진행 중인 이전 요청을 취소하기 위한 컨트롤러
  private _loadPageAbortController: AbortController | null = null
  // 필터/검색 변경 시 진행 중인 다음 페이지 요청을 무효화하기 위한 세대 토큰
  private _requestGeneration = 0
  private _isFetchingNextPage = false
  private _isLoadingFirstPage = false

  get isLastPage() {
    return this._pagination.params.isLast
  }

  isLoading = false

  async loadRecentSchools() {
    try {
      const schools = await schoolTestController.fetchRecentlyDownloadedSchools(10)
      runInAction(() => {
        this.recentSchools = schools
      })
    } catch (error) {
      // 최근 학교 로드 실패는 치명적이지 않으므로 조용히 실패 허용
      console.error('Failed to load recent schools:', error)
    }
  }

  // schoolTest
  private _schoolTestList: SchoolTest[] = []

  get schoolTestList() {
    return this._schoolTestList
  }

  async loadNextSchoolTestPage() {
    // 느린 응답 동안 sentinel이 다시 보여 중복 호출되면 같은 페이지가 중복 append 되므로 가드.
    // 첫 페이지를 다시 받는 중에는 같은 세대라 세대 검사로 걸러지지 않으므로 함께 막는다
    if (this.isLastPage || this._isFetchingNextPage || this._isLoadingFirstPage) return

    const nextPage = this._pagination.params.page + 1
    const params = this.makeSchoolTestParams()
    params.page = nextPage
    // 응답이 도착했을 때 필터/검색이 바뀌지 않았는지 검증하기 위한 세대 스냅샷
    const generation = this._requestGeneration
    this._isFetchingNextPage = true

    try {
      const { content: schoolTests, last } = await schoolTestController.fetchSchoolTestList(params)

      // 응답이 늦게 도착하는 사이 필터가 바뀌었으면(세대 불일치) stale 결과이므로 폐기
      if (generation !== this._requestGeneration) return

      // 성공 후에만 페이지 상태 반영
      runInAction(() => {
        this._schoolTestList = [...this._schoolTestList, ...schoolTests]
        this._pagination.setPage(nextPage)
        this._pagination.setLastPage(last)
      })
    } finally {
      // 무효화된 이전 요청이 새 목록의 진행 플래그를 풀지 않도록 같은 세대일 때만 해제
      runInAction(() => {
        if (generation === this._requestGeneration) this._isFetchingNextPage = false
      })
    }
  }

  async loadSchoolTestPage(
    params?: Parameters<typeof schoolTestController.fetchSchoolTestList>[0],
  ) {
    // 진행 중인 이전 요청이 있으면 취소
    this._loadPageAbortController?.abort()
    const abortController = new AbortController()
    this._loadPageAbortController = abortController
    // 페이지를 처음부터 다시 받으므로, 진행 중이던 다음 페이지 요청을 무효화
    this._requestGeneration += 1
    this._isFetchingNextPage = false
    this._isLoadingFirstPage = true

    this._pagination.resetPage()

    try {
      const { content: schoolTests, last } = await schoolTestController.fetchSchoolTestList(
        this.makeSchoolTestParams(params),
        abortController.signal,
      )

      runInAction(() => {
        this._schoolTestList = [...schoolTests]
        this._pagination.setLastPage(last)
      })
    } catch (error) {
      // 취소된 요청은 무시
      if (abortController.signal.aborted) return
      return Promise.reject(error)
    } finally {
      // 취소된 이전 요청이 새 첫 페이지 요청의 로딩 표시를 풀지 않게 한다
      if (this._loadPageAbortController === abortController) {
        runInAction(() => {
          this._isLoadingFirstPage = false
        })
      }
    }
  }

  private makeSchoolTestParams(params?: object) {
    const currentSchoolType = this.dropdownManager.currentChecked.schoolType?.id as SchoolType
    let revision: Revision | undefined = 'CURRICULUM_15'

    if (
      currentSchoolType === 'MIDDLE' &&
      this.dropdownManager.currentChecked.gradeSemester &&
      this.dropdownManager.currentChecked.gradeSemester.id !== 'ALL'
    ) {
      const item = curriculumCoreService.getItemByTrieKey(
        this.dropdownManager.currentChecked.gradeSemester.id,
      )
      revision = item?.revision ?? revision
    }
    if (
      currentSchoolType === 'HIGH' &&
      this.dropdownManager.currentChecked.highSchoolSubject &&
      this.dropdownManager.currentChecked.highSchoolSubject.id !== 'ALL'
    ) {
      const item = curriculumCoreService.getItemByTrieKey(
        this.dropdownManager.currentChecked.highSchoolSubject.id,
      )
      revision = item?.revision ?? revision
    }
    const _params: Parameters<typeof schoolTestController.fetchSchoolTestList>[0] = {
      size: this._pagination.params.size,
      page: this._pagination.params.page,
      schoolCodes: this.schoolSearchManager.checkedSchoolIds,
      schoolName: this.schoolSearchManager.searchText,
      revision,
      ...params,
    }

    const checkedDropdown = this.dropdownManager.currentChecked

    if (checkedDropdown.testType?.id !== 'ALL') {
      _params.schoolTestType = checkedDropdown.testType?.id as SchoolTestType
    }
    _params.schoolType = checkedDropdown.schoolType?.id as SchoolType

    if (checkedDropdown.testType?.id !== 'ALL') {
      _params.schoolTestType = checkedDropdown.testType?.id as SchoolTestType
    }

    if (
      currentSchoolType === 'MIDDLE' &&
      checkedDropdown.gradeSemester?.id &&
      checkedDropdown.gradeSemester?.id !== 'ALL'
    ) {
      const curriculumKey = checkedDropdown.gradeSemester.id
      const curriculumInfo = curriculumCoreService.getItemByTrieKey(curriculumKey)
      _params.grade = curriculumInfo?.gradeSubject
      _params.semester = curriculumInfo?.semester
    }
    if (
      currentSchoolType === 'HIGH' &&
      checkedDropdown.highSchoolSubject?.id &&
      checkedDropdown.highSchoolSubject?.id !== 'ALL'
    ) {
      const curriculumKey = checkedDropdown.highSchoolSubject.id
      const curriculumInfo = curriculumCoreService.getItemByTrieKey(curriculumKey)
      _params.grade = curriculumInfo?.gradeSubject
    }

    const checkedYear = this.dropdownManager.currentChecked.years
    if (checkedYear?.id !== 'ALL') {
      _params.years = checkedYear?.id ? [Number(checkedYear.id)] : undefined
    } else {
      const curriculumItem = this._getSelectedCurriculumItem()
      if (curriculumItem) _params.years = getVisibleSchoolTestYears(curriculumItem)
    }

    return _params
  }

  // schoolTest checkableCollection
  private _checkSchoolTestColl = new Set<number>()

  get checkedSchoolTestIds() {
    return [...this._checkSchoolTestColl.values()]
  }

  get checkedSchoolTestAmount() {
    return this._checkSchoolTestColl.size
  }

  hasCheckedId(id: number) {
    return this._checkSchoolTestColl.has(id)
  }

  updateCheckedSchoolTestIds(schoolTest: SchoolTestOrigin) {
    if (this._checkSchoolTestColl.has(schoolTest.id)) {
      this._checkSchoolTestColl.delete(schoolTest.id)
    } else {
      this._checkSchoolTestColl.add(schoolTest.id)
    }
  }

  clearCheckedSchoolTestIds() {
    this._checkSchoolTestColl.clear()
  }

  async applyEssential(filterOptions?: SchoolTestDropdownType) {
    // 기존 필터 초기화 하고 새로운 필터 적용해야함
    this.clearCheckedSchoolTestIds()
    this._schoolSearchManager.resetAllRegionSchoolFilters()
    if (filterOptions) {
      this._dropdownManager.updateFilterOptions(filterOptions)
      this._adjustYearFilterForCurriculum()
      localStorageService.setItem('THEME_SCHOOL_TEST_FILTER_OPTIONS')({
        ...localStorageService.getItem('THEME_SCHOOL_TEST_FILTER_OPTIONS'),
        ...filterOptions,
        // 학제, 학년/과목 필터 변경 시, 데이터 정합성을 지키기 위해 지역 학교 필터 관련 정보 초기화
        checkedSchools: undefined,
        sidoId: undefined,
        sigunguId: undefined,
      })
    }
    // TODO: 매번 schools를 부르는게 불필요한 동작이라고 생각, 학교 검색 팝오버를 열었을 때 새로운 schools를 불러오는 게 효율적
    await this.loadSchools()
    await this.loadSchoolTestPage()
  }

  async applyOptional(filterOptions?: SchoolTestDropdownType) {
    if (filterOptions) {
      this._dropdownManager.updateFilterOptions(filterOptions)
    }
    await this.loadSchoolTestPage()
  }

  async downloadSchoolTest(schoolTestId: number) {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.exportSchoolTests([schoolTestId])
        await delayNativeAppApiCall()
        return
      }
      const { presignedUrl, fileName } = await schoolTestController.fetchSchoolTestDownloadInfo({
        schoolTestIds: [schoolTestId],
        presignedUrlType: 'DOWNLOAD',
      })

      await fileDownload(presignedUrl, { fullFileName: fileName }, '_self')
      toastSuccess('학교별 기출을 다운로드 하였습니다.')
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  async downloadSchoolTestList() {
    if (WebviewService.isAppAndroid) {
      appPostMsg.exportSchoolTests(this.checkedSchoolTestIds)
      await delayNativeAppApiCall()
      return
    }
    const { presignedUrl, fileName } = await schoolTestController.fetchSchoolTestDownloadInfo({
      schoolTestIds: this.checkedSchoolTestIds,
      presignedUrlType: 'DOWNLOAD',
    })

    await fileDownload(presignedUrl, { fullFileName: fileName }, '_self')
    toastSuccess('학교별 기출을 다운로드 하였습니다.')
  }

  async reloadPdfAccessedStatusLocally(
    schoolTestWorkbookIds: number[] = this.checkedSchoolTestIds,
  ) {
    const checkedSchoolTestList = this._schoolTestList.filter((schoolTest) => {
      if (!schoolTest.schoolTestWorkbook) return false
      return schoolTestWorkbookIds.includes(schoolTest.schoolTestWorkbook.id)
    })

    for (const schoolTest of checkedSchoolTestList) {
      schoolTest.updatePdfAccessed()
    }

    this._schoolTestList = [...this._schoolTestList]
  }

  // similarWorksheets by schoolTest
  private _similarWorksheets: WorksheetBySchoolTest[] = []
  private _checkedSimilarWorksheetColl = new Set<number>()

  private _similarWorksheetCount: {
    sameSchool: number
    sameRegion: number
    neighborhoodRegion: number
    specialEducationRegion: number
  } = {
    neighborhoodRegion: 0,
    sameRegion: 0,
    sameSchool: 0,
    specialEducationRegion: 0,
  }

  get checkedSimilarWorksheetCount() {
    return this._checkedSimilarWorksheetColl.size
  }

  get similarWorksheetCount() {
    return this._similarWorksheetCount
  }

  get similarWorksheets() {
    return [...this._similarWorksheets]
  }

  hasCheckedSimilarWorksheetId(id: number) {
    return this._checkedSimilarWorksheetColl.has(id)
  }

  toggleCheckedSimilarWorksheetIds(id: number) {
    if (this._checkedSimilarWorksheetColl.has(id)) {
      this._checkedSimilarWorksheetColl.delete(id)
    } else {
      this._checkedSimilarWorksheetColl.add(id)
    }
  }

  async loadSimilarWorksheetsBySchoolTest(
    schoolTest: SchoolTest,
    searchType: SchoolTestSearchType,
  ) {
    this.isLoading = true
    const similarWorksheets = await worksheetController.fetchSimilarWorksheetsBySchoolTest({
      schoolCode: schoolTest.schoolCode,
      grade: schoolTest.grade,
      semester: schoolTest.semester ?? undefined,
      schoolTestType: schoolTest.schoolTestType,
      searchType,
    })

    const similarWorksheetBySchoolTest = similarWorksheets.map(
      (worksheet) => new WorksheetBySchoolTest(worksheet),
    )

    runInAction(() => {
      this._similarWorksheets = similarWorksheetBySchoolTest
      this.isLoading = false
    })
  }

  async loadSimilarWorksheetCount(schoolTest: SchoolTest) {
    const similarWorksheetCount = await worksheetController.fetchWorksheetCountBySchoolTest({
      schoolCode: schoolTest.schoolCode,
      grade: schoolTest.grade,
      semester: schoolTest.semester ?? undefined,
      schoolTestType: schoolTest.schoolTestType,
    })

    runInAction(() => {
      this._similarWorksheetCount = similarWorksheetCount
    })

    return similarWorksheetCount
  }

  async previewPDF(schoolTestId: number) {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.previewSchoolTests([schoolTestId])
        await delayNativeAppApiCall()
        return
      }
      const getPreviewUrl = async () => {
        const { presignedUrl, fileName } = await schoolTestController.fetchSchoolTestDownloadInfo({
          schoolTestIds: [schoolTestId],
          presignedUrlType: 'PREVIEW',
        })
        return { url: presignedUrl, fullFileName: fileName }
      }

      await urlPreview(getPreviewUrl)
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  async previewWorksheetPDF(worksheetId: number) {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.previewWorksheet(worksheetId)
        return
      }
      const getPreviewUrl = async () => {
        const { url, fullFileName } = await worksheetController.fetchWorksheetDownload({
          presignedUrlType: 'PREVIEW',
          worksheetIds: [worksheetId],
          worksheetPdfTypes: ['ORIGINAL'],
          isMergedPdf: true,
        })
        return { url, fullFileName }
      }

      await urlPreview(getPreviewUrl)
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  async printWorksheet() {
    await worksheetPrintService.onPrint(this.checkedSchoolTestIds)
  }

  async downloadWorksheet(
    worksheetId: number,
    downloadOptions: Pick<
      WorksheetFileParams,
      'isMergedPdf' | 'studentNames' | 'worksheetPdfTypes'
    >,
  ) {
    try {
      if (WebviewService.isAppAndroid) {
        appPostMsg.exportWorksheets({
          worksheetIds: [worksheetId],
          ...downloadOptions,
        })
        return
      }

      const { fullFileName, url } = await worksheetController.fetchWorksheetDownload({
        presignedUrlType: 'DOWNLOAD',
        worksheetIds: [worksheetId],
        ...downloadOptions,
      })
      await fileDownload(url, { fullFileName }, '_self')
      toastSuccess('학교별 기출을 다운로드 하였습니다.')
    } catch (error) {
      commonErrorMessage(error)
    }
  }

  async assignWorksheetsForStudents(callback?: () => void) {
    const fetchedWorksheets = await Promise.all(
      [...this._checkedSimilarWorksheetColl.values()].map((worksheetId) =>
        worksheetController.fetchWorksheetDetail(worksheetId),
      ),
    )

    const selectedWorksheets = fetchedWorksheets.map((worksheet) => {
      let newWorksheet = new Worksheet(worksheet)
      this._similarWorksheets.forEach((similarWorksheet) => {
        if (similarWorksheet.id === worksheet.id) {
          newWorksheet = new Worksheet({
            ...worksheet,
            assignList: similarWorksheet.assignedStudentWorksheetList,
          })
        }
      })
      return newWorksheet
    })

    commonWorksheetService.assignStudentWorksheetList(selectedWorksheets, (worksheetIds) => {
      for (const worksheetId of worksheetIds) {
        if (this.hasCheckedSimilarWorksheetId(worksheetId)) {
          this.toggleCheckedSimilarWorksheetIds(worksheetId)
        }
      }

      callback?.()
    })
  }

  resetCheckedWorksheets() {
    this._checkedSimilarWorksheetColl.clear()
  }

  // schoolSearchManager
  private _schoolSearchManager = new SchoolsUnderRegionStore()

  get schoolSearchManager() {
    return this._schoolSearchManager
  }

  // dropdownManager

  // 학교별 기출 필터에 여는 22개정 커리큘럼 키 (중3은 2027년 시험부터라 아직 제외)
  private static CURRICULUM_22_KEYS = new Set<string>([
    CURRICULUM_KEYS.CURRICULUM_KEY_중학교_1학년_1학기_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_중학교_1학년_2학기_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_중학교_2학년_1학기_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_중학교_2학년_2학기_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학1_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_공통수학2_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_대수_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_미적분1_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_확률과통계_22개정,
    CURRICULUM_KEYS.CURRICULUM_KEY_고등학교_기하_22개정,
  ])

  private _dropdownManager = new SchoolTestDropdownManager({
    middleSchoolGradeSemesterList: (() => {
      // 15개정 중학교 학년·학기 목록 (전체 옵션 포함)
      const revision15List = curriculumCoreService.getGradeSemesterMiddle({
        expandedLabel: '학년·학기 전체',
        revisions: ['CURRICULUM_15'],
      })
      // 22개정 중학교 학년·학기 중 현재 지원 대상만 Map으로 구성 (학년-학기 기준 매칭용)
      const revision22Map = new Map(
        curriculumCoreService
          .getGradeSemesterMiddle({ revisions: ['CURRICULUM_22'] })
          .filter((item) => WorksheetBySchoolTestService.CURRICULUM_22_KEYS.has(item.trieKey))
          .map((item) => [`${item.gradeSubject}-${item.semester}`, item]),
      )
      // 15개정 목록을 기준으로, 동일 학년·학기의 22개정 항목이 있으면 함께 포함
      return revision15List.flatMap((item): ExpendCoreItem[] => {
        if (item.isAllType) return [item]
        const matched22Item = revision22Map.get(`${item.gradeSubject}-${item.semester}`)
        return matched22Item ? [item, matched22Item] : [item]
      })
    })(),
    highSchoolSubjectList: [
      ...curriculumCoreService.getGradeSubjectHigh({
        revisions: ['CURRICULUM_15'],
      }),
      ...curriculumCoreService
        .getGradeSubjectHigh({
          revisions: ['CURRICULUM_22'],
        })
        .filter((item) => WorksheetBySchoolTestService.CURRICULUM_22_KEYS.has(item.trieKey)),
    ],
  })
  private _yearFilterOptions = new DropdownStore([
    new DropdownNode('ALL', '연도 전체'),
    ...SchoolTestDropdownManager.DEFAULT_YEARS.map(
      (year) => new DropdownNode(year.toString(), year.toString() + '년'),
    ),
  ])

  private _adjustYearFilterForCurriculum() {
    const filteredYears = this._getFilteredYearDropdownList()
    const currentCheckedYear = this._yearFilterOptions.dropdownList.find((n) => n.checked)

    if (currentCheckedYear && !filteredYears.some((n) => n.id === currentCheckedYear.id)) {
      const fallbackId = filteredYears[0]?.id ?? 'ALL'
      this._yearFilterOptions.updateCheckedNodes(fallbackId as string)
    }
  }

  private _getSelectedCurriculumKey(): string | undefined {
    const currentSchoolType = this._dropdownManager.currentChecked.schoolType?.id as
      | SchoolType
      | undefined

    if (currentSchoolType === 'MIDDLE') {
      const id = this._dropdownManager.currentChecked.gradeSemester?.id
      return id && id !== 'ALL' ? id : undefined
    }
    if (currentSchoolType === 'HIGH') {
      const id = this._dropdownManager.currentChecked.highSchoolSubject?.id
      return id && id !== 'ALL' ? id : undefined
    }
    return undefined
  }

  private _getSelectedCurriculumItem() {
    const selectedKey = this._getSelectedCurriculumKey()
    return selectedKey ? curriculumCoreService.getItemByTrieKey(selectedKey) : undefined
  }

  private _getFilteredYearDropdownList() {
    const allYears = this._yearFilterOptions.dropdownList
    const curriculumItem = this._getSelectedCurriculumItem()
    if (!curriculumItem) return allYears

    const visibleYears = getVisibleSchoolTestYears(curriculumItem)
    return allYears.filter((node) => node.id === 'ALL' || visibleYears.includes(Number(node.id)))
  }

  get dropdownManager() {
    const {
      currentChecked,
      gradeSemesterFilterOptions,
      highSchoolSubjectFilterOptions,
      schoolTypeOptions,
      testTypeFilterOption,
    } = this._dropdownManager

    const filteredYearList = this._getFilteredYearDropdownList()
    const yearList =
      filteredYearList.length > 0 ? filteredYearList : this._yearFilterOptions.dropdownList
    const checkedYear = yearList.find((node) => node.checked)

    const effectiveCheckedYear = checkedYear ?? yearList[0]

    return {
      currentChecked: {
        ...currentChecked,
        years: effectiveCheckedYear,
      },
      gradeSemesterFilterOptions,
      highSchoolSubjectFilterOptions,
      schoolTypeOptions,
      testTypeFilterOption,
      yearFilterOptions: {
        updateCheckedNodes: (id: string, options?: { multiple?: boolean }) =>
          this._yearFilterOptions.updateCheckedNodes(id, options),
        dropdownList: filteredYearList,
      },
    }
  }
}
